import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Settings, Save, Loader2, Plus, Pencil, Trash2,
  CreditCard, Crown, DollarSign, Wallet, Building2,
  X, Tag, Percent, Calendar, Grid3X3, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('plans');
  
  // Subscription Plans State
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: 0,
    duration_days: 365,
    max_members: 30,
    max_events: 1,
    features: [],
    is_active: true
  });
  const [featureInput, setFeatureInput] = useState('');
  const [savingPlan, setSavingPlan] = useState(false);

  // Payment Methods State
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [methodDialogOpen, setMethodDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [methodForm, setMethodForm] = useState({
    name: '',
    type: 'gcash',
    account_name: '',
    account_number: '',
    account_email: '',
    bank_name: '',
    instructions: '',
    is_active: true
  });
  const [savingMethod, setSavingMethod] = useState(false);

  // Promo Codes State
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(true);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: 0,
    valid_from: '',
    valid_until: '',
    applicable_plans: [],
    is_active: true
  });
  const [savingPromo, setSavingPromo] = useState(false);

  // Feature Matrix State
  const [featureMatrix, setFeatureMatrix] = useState([]);
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    category: '',
    icon: 'Calendar',
    features: []
  });
  const [newFeatureForm, setNewFeatureForm] = useState({
    name: '',
    basic: false,
    organized: false,
    transparency: false,
    license: false
  });

  const iconOptions = [
    'Calendar', 'Users', 'Building2', 'CreditCard', 'Megaphone',
    'FileText', 'ClipboardList', 'UserCheck', 'HelpCircle', 'Settings'
  ];

  useEffect(() => {
    fetchPlans();
    fetchPaymentMethods();
    fetchPromoCodes();
    fetchFeatureMatrix();
  }, []);

  // ===================== Subscription Plans =====================
  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/subscription-plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const openPlanDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name,
        description: plan.description || '',
        price: plan.price,
        duration_days: plan.duration_days || 365,
        max_members: plan.max_members || 30,
        max_events: plan.max_events || 1,
        features: plan.features || [],
        is_active: plan.is_active !== false
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: '',
        description: '',
        price: 0,
        duration_days: 365,
        max_members: 30,
        max_events: 1,
        features: [],
        is_active: true
      });
    }
    setFeatureInput('');
    setPlanDialogOpen(true);
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setPlanForm({
        ...planForm,
        features: [...planForm.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setPlanForm({
      ...planForm,
      features: planForm.features.filter((_, i) => i !== index)
    });
  };

  const savePlan = async () => {
    if (!planForm.name.trim()) {
      toast.error('Plan name is required');
      return;
    }
    
    setSavingPlan(true);
    try {
      if (editingPlan) {
        await axios.put(`${API_URL}/api/admin/subscription-plans/${editingPlan.id}`, planForm);
        toast.success('Plan updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/subscription-plans`, planForm);
        toast.success('Plan created successfully');
      }
      setPlanDialogOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/subscription-plans/${planId}`);
      toast.success('Plan deleted');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  // ===================== Payment Methods =====================
  const fetchPaymentMethods = async () => {
    setMethodsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/payment-methods`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    } finally {
      setMethodsLoading(false);
    }
  };

  const openMethodDialog = (method = null) => {
    if (method) {
      setEditingMethod(method);
      setMethodForm({
        name: method.name,
        type: method.type,
        account_name: method.account_name || '',
        account_number: method.account_number || '',
        account_email: method.account_email || '',
        bank_name: method.bank_name || '',
        instructions: method.instructions || '',
        is_active: method.is_active !== false
      });
    } else {
      setEditingMethod(null);
      setMethodForm({
        name: '',
        type: 'gcash',
        account_name: '',
        account_number: '',
        account_email: '',
        bank_name: '',
        instructions: '',
        is_active: true
      });
    }
    setMethodDialogOpen(true);
  };

  const saveMethod = async () => {
    if (!methodForm.name.trim()) {
      toast.error('Method name is required');
      return;
    }
    
    setSavingMethod(true);
    try {
      if (editingMethod) {
        await axios.put(`${API_URL}/api/admin/payment-methods/${editingMethod.id}`, methodForm);
        toast.success('Payment method updated');
      } else {
        await axios.post(`${API_URL}/api/admin/payment-methods`, methodForm);
        toast.success('Payment method created');
      }
      setMethodDialogOpen(false);
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment method');
    } finally {
      setSavingMethod(false);
    }
  };

  const deleteMethod = async (methodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/payment-methods/${methodId}`);
      toast.success('Payment method deleted');
      fetchPaymentMethods();
    } catch (error) {
      toast.error('Failed to delete payment method');
    }
  };

  // ===================== Promo Codes =====================
  const fetchPromoCodes = async () => {
    setPromoLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/promo-codes`);
      setPromoCodes(response.data);
    } catch (error) {
      console.error('Failed to load promo codes:', error);
    } finally {
      setPromoLoading(false);
    }
  };

  const openPromoDialog = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setPromoForm({
        code: promo.code,
        description: promo.description || '',
        discount_type: promo.discount_type || 'percentage',
        discount_value: promo.discount_value || 10,
        max_uses: promo.max_uses || 0,
        valid_from: promo.valid_from ? promo.valid_from.split('T')[0] : '',
        valid_until: promo.valid_until ? promo.valid_until.split('T')[0] : '',
        applicable_plans: promo.applicable_plans || [],
        is_active: promo.is_active !== false
      });
    } else {
      setEditingPromo(null);
      setPromoForm({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 10,
        max_uses: 0,
        valid_from: '',
        valid_until: '',
        applicable_plans: [],
        is_active: true
      });
    }
    setPromoDialogOpen(true);
  };

  const savePromo = async () => {
    if (!promoForm.code.trim()) {
      toast.error('Promo code is required');
      return;
    }
    if (promoForm.discount_value <= 0) {
      toast.error('Discount value must be greater than 0');
      return;
    }
    
    setSavingPromo(true);
    try {
      const payload = {
        ...promoForm,
        valid_from: promoForm.valid_from ? new Date(promoForm.valid_from).toISOString() : null,
        valid_until: promoForm.valid_until ? new Date(promoForm.valid_until).toISOString() : null
      };
      
      if (editingPromo) {
        await axios.put(`${API_URL}/api/admin/promo-codes/${editingPromo.id}`, payload);
        toast.success('Promo code updated');
      } else {
        await axios.post(`${API_URL}/api/admin/promo-codes`, payload);
        toast.success('Promo code created');
      }
      setPromoDialogOpen(false);
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promo code');
    } finally {
      setSavingPromo(false);
    }
  };

  const deletePromo = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/promo-codes/${promoId}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  // ===================== Feature Matrix =====================
  const fetchFeatureMatrix = async () => {
    setMatrixLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/feature-matrix`);
      setFeatureMatrix(response.data.categories || []);
    } catch (error) {
      console.error('Failed to load feature matrix:', error);
    } finally {
      setMatrixLoading(false);
    }
  };

  const openCategoryDialog = (category = null, index = null) => {
    if (category) {
      setEditingCategory(category);
      setEditingCategoryIndex(index);
      setCategoryForm({
        category: category.category,
        icon: category.icon,
        features: [...category.features]
      });
    } else {
      setEditingCategory(null);
      setEditingCategoryIndex(null);
      setCategoryForm({
        category: '',
        icon: 'Calendar',
        features: []
      });
    }
    setNewFeatureForm({ name: '', basic: false, organized: false, transparency: false, license: false });
    setEditCategoryOpen(true);
  };

  const addFeatureToCategory = () => {
    if (!newFeatureForm.name.trim()) {
      toast.error('Feature name is required');
      return;
    }
    setCategoryForm({
      ...categoryForm,
      features: [...categoryForm.features, { ...newFeatureForm }]
    });
    setNewFeatureForm({ name: '', basic: false, organized: false, transparency: false, license: false });
  };

  const removeFeatureFromCategory = (index) => {
    setCategoryForm({
      ...categoryForm,
      features: categoryForm.features.filter((_, i) => i !== index)
    });
  };

  const updateFeatureValue = (featureIndex, tier, value) => {
    const updatedFeatures = [...categoryForm.features];
    updatedFeatures[featureIndex] = {
      ...updatedFeatures[featureIndex],
      [tier]: value
    };
    setCategoryForm({ ...categoryForm, features: updatedFeatures });
  };

  const saveCategory = () => {
    if (!categoryForm.category.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    const updatedMatrix = [...featureMatrix];
    if (editingCategoryIndex !== null) {
      updatedMatrix[editingCategoryIndex] = categoryForm;
    } else {
      updatedMatrix.push(categoryForm);
    }
    setFeatureMatrix(updatedMatrix);
    setEditCategoryOpen(false);
    toast.success(editingCategoryIndex !== null ? 'Category updated' : 'Category added');
  };

  const deleteCategory = (index) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    const updatedMatrix = featureMatrix.filter((_, i) => i !== index);
    setFeatureMatrix(updatedMatrix);
    toast.success('Category deleted');
  };

  const saveFeatureMatrix = async () => {
    setSavingMatrix(true);
    try {
      await axios.put(`${API_URL}/api/admin/feature-matrix`, { categories: featureMatrix });
      toast.success('Feature matrix saved successfully');
    } catch (error) {
      toast.error('Failed to save feature matrix');
    } finally {
      setSavingMatrix(false);
    }
  };

  const getMethodIcon = (type) => {
    switch (type) {
      case 'paypal': return <Wallet className="h-4 w-4 text-blue-500" />;
      case 'gcash': return <Wallet className="h-4 w-4 text-blue-400" />;
      case 'bank': return <Building2 className="h-4 w-4 text-green-500" />;
      case 'cash': return <DollarSign className="h-4 w-4 text-amber-500" />;
      default: return <CreditCard className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-settings">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="h-8 w-8" />
            Platform Settings
          </h1>
          <p className="text-gray-400 mt-1">
            Manage subscription plans, payment methods, and promo codes
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-800">
            <TabsTrigger value="plans" className="data-[state=active]:bg-indigo-600" data-testid="tab-plans">
              <Crown className="h-4 w-4 mr-2" />
              Subscription Plans
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-indigo-600" data-testid="tab-features">
              <Grid3X3 className="h-4 w-4 mr-2" />
              Feature Matrix
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-indigo-600" data-testid="tab-payments">
              <CreditCard className="h-4 w-4 mr-2" />
              Payment Methods
            </TabsTrigger>
            <TabsTrigger value="promos" className="data-[state=active]:bg-indigo-600" data-testid="tab-promos">
              <Tag className="h-4 w-4 mr-2" />
              Promo Codes
            </TabsTrigger>
          </TabsList>

          {/* Subscription Plans Tab */}
          <TabsContent value="plans" className="mt-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Subscription Plans</CardTitle>
                  <CardDescription className="text-gray-400">
                    Configure plans that organizers can subscribe to
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => openPlanDialog()} 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="add-plan-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Plan
                </Button>
              </CardHeader>
              <CardContent>
                {plansLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                  </div>
                ) : plans.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Crown className="h-12 w-12 mx-auto mb-3 text-gray-600" />
                    <p>No subscription plans yet</p>
                    <p className="text-sm">Create your first plan to get started</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700 hover:bg-gray-700/50">
                        <TableHead className="text-gray-400">Plan Name</TableHead>
                        <TableHead className="text-gray-400">Price</TableHead>
                        <TableHead className="text-gray-400">Duration</TableHead>
                        <TableHead className="text-gray-400">Limits</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {plans.map((plan) => (
                        <TableRow key={plan.id} className="border-gray-700 hover:bg-gray-700/50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-white">{plan.name}</p>
                              {plan.description && (
                                <p className="text-xs text-gray-500 truncate max-w-[200px]">{plan.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-bold text-gold">{formatCurrency(plan.price)}</TableCell>
                          <TableCell className="text-gray-300">{plan.duration_days} days</TableCell>
                          <TableCell className="text-gray-300">
                            <div className="text-xs">
                              <p>{plan.max_members} members</p>
                              <p>{plan.max_events} events</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={plan.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                              {plan.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPlanDialog(plan)}
                              className="text-gray-400 hover:text-white"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deletePlan(plan.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Matrix Tab */}
          <TabsContent value="features" className="mt-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Feature Comparison Matrix</CardTitle>
                  <CardDescription className="text-gray-400">
                    Configure which features are available in each subscription tier
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => openCategoryDialog()} 
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="add-category-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                  <Button 
                    onClick={saveFeatureMatrix}
                    disabled={savingMatrix}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="save-matrix-btn"
                  >
                    {savingMatrix ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {matrixLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                  </div>
                ) : featureMatrix.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Grid3X3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No feature categories configured</p>
                    <p className="text-sm">Add categories to define features for each subscription tier</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Header Row */}
                    <div className="grid grid-cols-6 gap-2 text-xs font-semibold text-gray-400 pb-2 border-b border-gray-700">
                      <div className="col-span-2">Feature</div>
                      <div className="text-center">Basic</div>
                      <div className="text-center">Organized</div>
                      <div className="text-center">Transparency</div>
                      <div className="text-center">License</div>
                    </div>
                    
                    {featureMatrix.map((category, catIndex) => (
                      <div key={catIndex} className="bg-gray-900 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-white font-semibold flex items-center gap-2">
                            <span className="text-indigo-400">{category.icon}</span>
                            {category.category}
                          </h3>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openCategoryDialog(category, catIndex)}
                              className="text-gray-400 hover:text-white"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteCategory(catIndex)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {category.features.map((feature, featIndex) => (
                          <div key={featIndex} className="grid grid-cols-6 gap-2 py-2 border-t border-gray-800 text-sm">
                            <div className="col-span-2 text-gray-300">{feature.name}</div>
                            <div className="text-center">
                              {typeof feature.basic === 'boolean' ? (
                                feature.basic ? <Check className="h-4 w-4 text-green-500 mx-auto" /> : <X className="h-4 w-4 text-gray-600 mx-auto" />
                              ) : (
                                <span className="text-amber-400 text-xs">{feature.basic}</span>
                              )}
                            </div>
                            <div className="text-center">
                              {typeof feature.organized === 'boolean' ? (
                                feature.organized ? <Check className="h-4 w-4 text-green-500 mx-auto" /> : <X className="h-4 w-4 text-gray-600 mx-auto" />
                              ) : (
                                <span className="text-amber-400 text-xs">{feature.organized}</span>
                              )}
                            </div>
                            <div className="text-center">
                              {typeof feature.transparency === 'boolean' ? (
                                feature.transparency ? <Check className="h-4 w-4 text-green-500 mx-auto" /> : <X className="h-4 w-4 text-gray-600 mx-auto" />
                              ) : (
                                <span className="text-amber-400 text-xs">{feature.transparency}</span>
                              )}
                            </div>
                            <div className="text-center">
                              {typeof feature.license === 'boolean' ? (
                                feature.license ? <Check className="h-4 w-4 text-green-500 mx-auto" /> : <X className="h-4 w-4 text-gray-600 mx-auto" />
                              ) : (
                                <span className="text-amber-400 text-xs">{feature.license}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Methods Tab */}
          <TabsContent value="payments" className="mt-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Payment Methods</CardTitle>
                  <CardDescription className="text-gray-400">
                    Configure payment options that organizers can use
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => openMethodDialog()} 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="add-method-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Method
                </Button>
              </CardHeader>
              <CardContent>
                {methodsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                  </div>
                ) : paymentMethods.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-600" />
                    <p>No payment methods yet</p>
                    <p className="text-sm">Add payment methods for organizers</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700 hover:bg-gray-700/50">
                        <TableHead className="text-gray-400">Method</TableHead>
                        <TableHead className="text-gray-400">Account Details</TableHead>
                        <TableHead className="text-gray-400">Instructions</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentMethods.map((method) => (
                        <TableRow key={method.id} className="border-gray-700 hover:bg-gray-700/50">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getMethodIcon(method.type)}
                              <span className="font-medium text-white">{method.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            <div className="text-xs space-y-1">
                              {method.account_name && <p>Name: {method.account_name}</p>}
                              {method.account_number && <p>Number: {method.account_number}</p>}
                              {method.account_email && <p>Email: {method.account_email}</p>}
                              {method.bank_name && <p>Bank: {method.bank_name}</p>}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-400 text-sm max-w-[200px] truncate">
                            {method.instructions || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={method.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                              {method.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openMethodDialog(method)}
                              className="text-gray-400 hover:text-white"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteMethod(method.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promos" className="mt-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Promo Codes</CardTitle>
                  <CardDescription className="text-gray-400">
                    Create discount codes for organizer subscriptions
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => openPromoDialog()} 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="add-promo-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Promo Code
                </Button>
              </CardHeader>
              <CardContent>
                {promoLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                  </div>
                ) : promoCodes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Tag className="h-12 w-12 mx-auto mb-3 text-gray-600" />
                    <p>No promo codes yet</p>
                    <p className="text-sm">Create promo codes to offer discounts</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700 hover:bg-gray-700/50">
                        <TableHead className="text-gray-400">Code</TableHead>
                        <TableHead className="text-gray-400">Discount</TableHead>
                        <TableHead className="text-gray-400">Usage</TableHead>
                        <TableHead className="text-gray-400">Validity</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {promoCodes.map((promo) => (
                        <TableRow key={promo.id} className="border-gray-700 hover:bg-gray-700/50">
                          <TableCell>
                            <div>
                              <p className="font-mono font-bold text-gold">{promo.code}</p>
                              {promo.description && (
                                <p className="text-xs text-gray-500 truncate max-w-[150px]">{promo.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-purple-500/20 text-purple-400">
                              {promo.discount_type === 'percentage' ? (
                                <><Percent className="h-3 w-3 mr-1" />{promo.discount_value}%</>
                              ) : (
                                <><DollarSign className="h-3 w-3 mr-1" />{formatCurrency(promo.discount_value)}</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            <div className="text-sm">
                              <span className="text-white">{promo.used_count || 0}</span>
                              <span className="text-gray-500"> / {promo.max_uses || '∞'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-400 text-xs">
                            {promo.valid_from || promo.valid_until ? (
                              <div>
                                {promo.valid_from && <p>From: {formatDate(promo.valid_from)}</p>}
                                {promo.valid_until && <p>Until: {formatDate(promo.valid_until)}</p>}
                              </div>
                            ) : (
                              <span>No expiry</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={promo.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                              {promo.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPromoDialog(promo)}
                              className="text-gray-400 hover:text-white"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deletePromo(promo.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Plan Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-gold">
              {editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure the plan details and features
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Plan Name *</Label>
              <Input
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                placeholder="e.g., Premium Plan"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="plan-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                placeholder="Brief description of the plan"
                className="bg-gray-800 border-gray-700 text-white"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Price (PHP)</Label>
                <Input
                  type="number"
                  value={planForm.price}
                  onChange={(e) => setPlanForm({ ...planForm, price: parseFloat(e.target.value) || 0 })}
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="plan-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Duration (Days)</Label>
                <Input
                  type="number"
                  value={planForm.duration_days}
                  onChange={(e) => setPlanForm({ ...planForm, duration_days: parseInt(e.target.value) || 365 })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Max Members</Label>
                <Input
                  type="number"
                  value={planForm.max_members}
                  onChange={(e) => setPlanForm({ ...planForm, max_members: parseInt(e.target.value) || 30 })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Max Events</Label>
                <Input
                  type="number"
                  value={planForm.max_events}
                  onChange={(e) => setPlanForm({ ...planForm, max_events: parseInt(e.target.value) || 1 })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Features</Label>
              <div className="flex gap-2">
                <Input
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  placeholder="Add a feature"
                  className="bg-gray-800 border-gray-700 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <Button type="button" onClick={addFeature} variant="outline" className="border-gray-700">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {planForm.features.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {planForm.features.map((feature, idx) => (
                    <Badge 
                      key={idx} 
                      variant="secondary" 
                      className="bg-gray-700 text-gray-200 flex items-center gap-1"
                    >
                      {feature}
                      <button onClick={() => removeFeature(idx)} className="hover:text-red-400">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-300">Active</Label>
              <Switch
                checked={planForm.is_active}
                onCheckedChange={(checked) => setPlanForm({ ...planForm, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)} className="border-gray-700 text-gray-300">
              Cancel
            </Button>
            <Button onClick={savePlan} disabled={savingPlan} className="bg-indigo-600 hover:bg-indigo-700">
              {savingPlan ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingPlan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog open={methodDialogOpen} onOpenChange={setMethodDialogOpen}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-gold">
              {editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure payment account details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Method Name *</Label>
              <Input
                value={methodForm.name}
                onChange={(e) => setMethodForm({ ...methodForm, name: e.target.value })}
                placeholder="e.g., GCash Payment"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="method-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Type</Label>
              <Select
                value={methodForm.type}
                onValueChange={(value) => setMethodForm({ ...methodForm, type: value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="gcash">GCash</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Account Name</Label>
              <Input
                value={methodForm.account_name}
                onChange={(e) => setMethodForm({ ...methodForm, account_name: e.target.value })}
                placeholder="Account holder name"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            {methodForm.type !== 'cash' && (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-300">
                    {methodForm.type === 'paypal' ? 'PayPal Email' : 'Account Number'}
                  </Label>
                  <Input
                    value={methodForm.type === 'paypal' ? methodForm.account_email : methodForm.account_number}
                    onChange={(e) => setMethodForm({ 
                      ...methodForm, 
                      [methodForm.type === 'paypal' ? 'account_email' : 'account_number']: e.target.value 
                    })}
                    placeholder={methodForm.type === 'paypal' ? 'email@example.com' : '0917-XXX-XXXX'}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                {methodForm.type === 'bank' && (
                  <div className="space-y-2">
                    <Label className="text-gray-300">Bank Name</Label>
                    <Input
                      value={methodForm.bank_name}
                      onChange={(e) => setMethodForm({ ...methodForm, bank_name: e.target.value })}
                      placeholder="e.g., BDO, BPI, UnionBank"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                )}
              </>
            )}

            <div className="space-y-2">
              <Label className="text-gray-300">Instructions</Label>
              <Textarea
                value={methodForm.instructions}
                onChange={(e) => setMethodForm({ ...methodForm, instructions: e.target.value })}
                placeholder="Payment instructions for users..."
                className="bg-gray-800 border-gray-700 text-white"
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-300">Active</Label>
              <Switch
                checked={methodForm.is_active}
                onCheckedChange={(checked) => setMethodForm({ ...methodForm, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMethodDialogOpen(false)} className="border-gray-700 text-gray-300">
              Cancel
            </Button>
            <Button onClick={saveMethod} disabled={savingMethod} className="bg-indigo-600 hover:bg-indigo-700">
              {savingMethod ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingMethod ? 'Update Method' : 'Add Method'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promo Code Dialog */}
      <Dialog open={promoDialogOpen} onOpenChange={setPromoDialogOpen}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-gold">
              {editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure discount code details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Promo Code *</Label>
              <Input
                value={promoForm.code}
                onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                placeholder="e.g., REUNION2025"
                className="bg-gray-800 border-gray-700 text-white font-mono uppercase"
                data-testid="promo-code-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Input
                value={promoForm.description}
                onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                placeholder="e.g., Early bird discount"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Discount Type</Label>
                <Select
                  value={promoForm.discount_type}
                  onValueChange={(value) => setPromoForm({ ...promoForm, discount_type: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (PHP)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">
                  Discount Value {promoForm.discount_type === 'percentage' ? '(%)' : '(PHP)'}
                </Label>
                <Input
                  type="number"
                  value={promoForm.discount_value}
                  onChange={(e) => setPromoForm({ ...promoForm, discount_value: parseFloat(e.target.value) || 0 })}
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="promo-discount-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Max Uses (0 = unlimited)</Label>
              <Input
                type="number"
                value={promoForm.max_uses}
                onChange={(e) => setPromoForm({ ...promoForm, max_uses: parseInt(e.target.value) || 0 })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Valid From</Label>
                <Input
                  type="date"
                  value={promoForm.valid_from}
                  onChange={(e) => setPromoForm({ ...promoForm, valid_from: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Valid Until</Label>
                <Input
                  type="date"
                  value={promoForm.valid_until}
                  onChange={(e) => setPromoForm({ ...promoForm, valid_until: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-300">Active</Label>
              <Switch
                checked={promoForm.is_active}
                onCheckedChange={(checked) => setPromoForm({ ...promoForm, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoDialogOpen(false)} className="border-gray-700 text-gray-300">
              Cancel
            </Button>
            <Button onClick={savePromo} disabled={savingPromo} className="bg-indigo-600 hover:bg-indigo-700">
              {savingPromo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingPromo ? 'Update Code' : 'Create Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editCategoryOpen} onOpenChange={setEditCategoryOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Grid3X3 className="h-5 w-5 text-indigo-400" />
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure features for this category across all subscription tiers
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Category Name *</Label>
                <Input
                  value={categoryForm.category}
                  onChange={(e) => setCategoryForm({ ...categoryForm, category: e.target.value })}
                  placeholder="e.g., Event Management"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Icon</Label>
                <Select
                  value={categoryForm.icon}
                  onValueChange={(value) => setCategoryForm({ ...categoryForm, icon: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(icon => (
                      <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Features List */}
            <div className="space-y-2">
              <Label className="text-gray-300">Features</Label>
              <p className="text-xs text-gray-500">Click checkbox for ✓, leave unchecked for ✗, or type custom text</p>
              
              {categoryForm.features.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-9 gap-2 text-xs font-semibold text-gray-400 pb-2 border-b border-gray-700">
                    <div className="col-span-3">Feature Name</div>
                    <div className="text-center">Basic</div>
                    <div className="text-center">Text</div>
                    <div className="text-center">Organized</div>
                    <div className="text-center">Transparency</div>
                    <div className="text-center">License</div>
                    <div></div>
                  </div>
                  
                  {categoryForm.features.map((feature, index) => (
                    <div key={index} className="grid grid-cols-9 gap-2 items-center py-2 border-b border-gray-700 last:border-0">
                      <div className="col-span-3">
                        <Input
                          value={feature.name}
                          onChange={(e) => {
                            const updatedFeatures = [...categoryForm.features];
                            updatedFeatures[index] = { ...updatedFeatures[index], name: e.target.value };
                            setCategoryForm({ ...categoryForm, features: updatedFeatures });
                          }}
                          className="bg-gray-700 border-gray-600 text-white text-sm h-8"
                        />
                      </div>
                      {/* Basic - Checkbox or Text */}
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          checked={feature.basic === true}
                          onChange={(e) => updateFeatureValue(index, 'basic', e.target.checked)}
                          className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500 cursor-pointer"
                        />
                      </div>
                      <div>
                        <Input
                          value={typeof feature.basic === 'string' ? feature.basic : ''}
                          onChange={(e) => updateFeatureValue(index, 'basic', e.target.value || false)}
                          placeholder="or text"
                          className="bg-gray-700 border-gray-600 text-white text-xs h-7 text-center"
                        />
                      </div>
                      {/* Organized */}
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          checked={feature.organized === true}
                          onChange={(e) => updateFeatureValue(index, 'organized', e.target.checked)}
                          className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500 cursor-pointer"
                        />
                      </div>
                      {/* Transparency */}
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          checked={feature.transparency === true}
                          onChange={(e) => updateFeatureValue(index, 'transparency', e.target.checked)}
                          className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500 cursor-pointer"
                        />
                      </div>
                      {/* License */}
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          checked={feature.license === true}
                          onChange={(e) => updateFeatureValue(index, 'license', e.target.checked)}
                          className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500 cursor-pointer"
                        />
                      </div>
                      {/* Delete */}
                      <div className="flex justify-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFeatureFromCategory(index)}
                          className="text-red-400 hover:text-red-300 p-1 h-6 w-6"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Feature */}
              <div className="bg-gray-800 rounded-lg p-3 mt-2">
                <p className="text-xs text-gray-400 mb-3">Add new feature - check boxes for included features</p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-gray-400 text-xs">Feature Name</Label>
                    <Input
                      value={newFeatureForm.name}
                      onChange={(e) => setNewFeatureForm({ ...newFeatureForm, name: e.target.value })}
                      placeholder="e.g., Event Dashboard"
                      className="bg-gray-700 border-gray-600 text-white text-sm mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newFeatureForm.basic === true}
                        onChange={(e) => setNewFeatureForm({ ...newFeatureForm, basic: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                      />
                      <span className="text-gray-300 text-sm">Basic</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newFeatureForm.organized === true}
                        onChange={(e) => setNewFeatureForm({ ...newFeatureForm, organized: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                      />
                      <span className="text-gray-300 text-sm">Organized</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newFeatureForm.transparency === true}
                        onChange={(e) => setNewFeatureForm({ ...newFeatureForm, transparency: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                      />
                      <span className="text-gray-300 text-sm">Transparency</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newFeatureForm.license === true}
                        onChange={(e) => setNewFeatureForm({ ...newFeatureForm, license: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                      />
                      <span className="text-gray-300 text-sm">License</span>
                    </label>
                  </div>
                </div>
                <Button
                  onClick={addFeatureToCategory}
                  className="mt-3 bg-indigo-600 hover:bg-indigo-700 w-full"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Feature
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCategoryOpen(false)} className="border-gray-700 text-gray-300">
              Cancel
            </Button>
            <Button onClick={saveCategory} className="bg-indigo-600 hover:bg-indigo-700">
              <Save className="h-4 w-4 mr-2" />
              {editingCategory ? 'Update Category' : 'Add Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminSettings;
